/**
 * Voyager 3D model (Sprint 2, S2-15): built procedurally from primitives in the spacecraft's real proportions (metres)
 * — no download, no licence question (deviation from plan D11, which assumed NASA's glTF). The high-gain antenna
 * points along +Z; the caller turns +Z towards Earth.
 *
 * Parts: 3.66 m parabolic high-gain antenna with feed and struts, decagonal bus, RTG boom with three radioisotope
 * generators, science boom with scan platform, 13 m magnetometer boom.
 */
import * as THREE from 'three';

/** Largest extent of the model without the magnetometer boom (m), for on-screen sizing. */
export const MODEL_SIZE_M = 4.5;

export function createVoyagerModel(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'voyager-model';
  // a faint self-illumination keeps the silhouette readable when the Sun is behind the spacecraft
  const material = (color: number, roughness: number, metalness: number): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({
      color,
      roughness,
      metalness,
      emissive: new THREE.Color(color).multiplyScalar(0.14),
    });
  const white = material(0xe9e4d8, 0.55, 0.05);
  const foil = material(0x9c7a35, 0.35, 0.7);
  const grey = material(0x8f8f8f, 0.6, 0.4);
  const dark = material(0x3a3a3a, 0.7, 0.3);

  // high-gain antenna: paraboloid z = r² / (4f), 1.83 m radius, focal length 0.8 m, opening towards +Z
  const f = 0.8;
  const rim = 1.83;
  const profile: THREE.Vector2[] = [];
  for (let i = 0; i <= 12; i++) {
    const r = (rim * i) / 12;
    profile.push(new THREE.Vector2(r, (r * r) / (4 * f)));
  }
  const dish = new THREE.Mesh(new THREE.LatheGeometry(profile, 40), white);
  dish.material.side = THREE.DoubleSide;
  dish.rotation.x = Math.PI / 2; // lathe axis is Y; turn it to Z
  dish.position.z = 0.25;
  root.add(dish);
  // feed (sub-reflector) on three struts
  const feed = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.08, 16), grey);
  feed.rotation.x = Math.PI / 2;
  feed.position.z = 0.25 + f;
  root.add(feed);
  for (let k = 0; k < 3; k++) {
    const a = (k / 3) * Math.PI * 2;
    const from = new THREE.Vector3(Math.cos(a) * 1.2, Math.sin(a) * 1.2, 0.25 + (1.2 * 1.2) / (4 * f));
    const to = new THREE.Vector3(0, 0, 0.25 + f);
    root.add(rod(from, to, 0.015, grey));
  }

  // decagonal bus below the dish
  const bus = new THREE.Mesh(new THREE.CylinderGeometry(0.89, 0.89, 0.47, 10), foil);
  bus.rotation.x = Math.PI / 2;
  bus.position.z = -0.05;
  root.add(bus);

  // RTG boom towards −X with three generators
  root.add(rod(new THREE.Vector3(-0.8, 0, -0.1), new THREE.Vector3(-3.0, 0, -0.35), 0.03, grey));
  for (let k = 0; k < 3; k++) {
    const rtg = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.5, 12), dark);
    rtg.rotation.z = Math.PI / 2;
    rtg.position.set(-1.6 - k * 0.55, 0, -0.2 - k * 0.05);
    root.add(rtg);
  }

  // science boom towards +X with the scan platform (cameras)
  root.add(rod(new THREE.Vector3(0.8, 0, -0.1), new THREE.Vector3(2.4, 0, -0.4), 0.03, grey));
  const platform = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.35, 0.35), dark);
  platform.position.set(2.5, 0, -0.45);
  root.add(platform);
  const camera = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.5, 10), grey);
  camera.rotation.x = Math.PI / 2;
  camera.position.set(2.5, 0.15, -0.75);
  root.add(camera);

  // magnetometer boom: 13 m, very thin, towards +Y
  root.add(rod(new THREE.Vector3(0, 0.8, -0.1), new THREE.Vector3(0, 13, -0.6), 0.012, grey));

  return root;
}

/** A thin cylinder from `a` to `b`. */
function rod(a: THREE.Vector3, b: THREE.Vector3, radius: number, material: THREE.Material): THREE.Mesh {
  const dir = new THREE.Vector3().subVectors(b, a);
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, dir.length(), 6), material);
  mesh.position.copy(a).addScaledVector(dir, 0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  return mesh;
}
